import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2,
  Users,
  ClipboardCheck,
  GraduationCap,
  UserMinus,
  Search,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Settings,
  FileText,
  Shield,
  Target,
  Bell,
  BarChart3,
  HelpCircle,
  Lightbulb,
  CheckCircle2,
  ArrowRight,
  User,
  Brain,
  MessageSquare,
  Mail,
  PieChart,
  AlertTriangle,
  Download,
  Send,
  ListChecks
} from 'lucide-react';

const HelpCenter = () => {
  const { user } = useAuth();
  const isEmployee = user?.role === 'funcionario';
  
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    gestaoSST: !isEmployee,
    empresa: false,
    funcionarios: false,
    avaliacoes: !isEmployee,
    aep: false,
    treinamentos: !isEmployee,
    dashboards: false,
    afastamentos: false,
    denuncias: false,
    meuPerfil: isEmployee,
    minhasAvaliacoes: isEmployee,
    meusTreinamentos: isEmployee
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Seções para FUNCIONÁRIOS
  const employeeSections = [
    {
      id: 'meuPerfil',
      title: 'Meu Perfil',
      icon: User,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      content: [
        {
          title: 'Visualizar Meus Dados',
          description: 'Veja suas informações pessoais e profissionais cadastradas.',
          steps: [
            'Clique no seu nome no canto superior direito',
            'Selecione "Meu Perfil"',
            'Visualize seus dados pessoais, cargo e setor',
            'Veja sua pontuação na gamificação'
          ]
        },
        {
          title: 'Atualizar Dados de Contato',
          description: 'Mantenha seu telefone e email atualizados para receber notificações.',
          steps: [
            'Acesse "Meu Perfil" no menu',
            'Clique em "Editar" nos dados de contato',
            'Atualize telefone, WhatsApp ou email',
            'Salve as alterações'
          ]
        },
        {
          title: 'Alterar Senha',
          description: 'Mantenha sua conta segura alterando a senha periodicamente.',
          steps: [
            'Acesse "Meu Perfil"',
            'Clique em "Alterar Senha"',
            'Digite a senha atual e a nova senha',
            'Confirme a nova senha e salve'
          ]
        }
      ]
    },
    {
      id: 'minhasAvaliacoes',
      title: 'Minhas Avaliações',
      icon: ClipboardCheck,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      content: [
        {
          title: 'Como Responder uma Avaliação',
          description: 'Passo a passo para responder avaliações de SST enviadas pela empresa.',
          steps: [
            'Você receberá um link por email ou WhatsApp',
            'Clique no link para abrir a avaliação',
            'Leia cada pergunta com atenção',
            'Responda todas as perguntas obrigatórias (marcadas com *)',
            'Clique em "Enviar Respostas" ao finalizar'
          ]
        },
        {
          title: 'Avaliações Pendentes',
          description: 'Veja quais avaliações você ainda precisa responder.',
          steps: [
            'Acesse "Minhas Avaliações" no menu lateral',
            'Avaliações pendentes aparecem com status "Pendente"',
            'Clique em "Responder" para iniciar',
            'Você pode salvar e continuar depois (se disponível)'
          ]
        },
        {
          title: 'Histórico de Respostas',
          description: 'Consulte avaliações que você já respondeu.',
          steps: [
            'Em "Minhas Avaliações", veja a lista completa',
            'Avaliações respondidas mostram data de conclusão',
            'Suas respostas são confidenciais (LGPD)',
            'Apenas estatísticas agregadas são compartilhadas com a empresa'
          ]
        },
        {
          title: 'Anonimato e Privacidade',
          description: 'Entenda como suas respostas são protegidas.',
          steps: [
            'Suas respostas individuais são confidenciais',
            'A empresa vê apenas estatísticas gerais do setor',
            'Nenhum gestor tem acesso às suas respostas específicas',
            'Responda com sinceridade para ajudar a melhorar o ambiente de trabalho'
          ]
        }
      ]
    },
    {
      id: 'meusTreinamentos',
      title: 'Meus Treinamentos',
      icon: GraduationCap,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      content: [
        {
          title: 'Visualizar Treinamentos',
          description: 'Veja todos os treinamentos disponíveis e seu progresso.',
          steps: [
            'Acesse "Meus Treinamentos" no menu lateral',
            'Veja a lista de treinamentos atribuídos a você',
            'Treinamentos obrigatórios aparecem destacados',
            'Acompanhe seu progresso e status de conclusão'
          ]
        },
        {
          title: 'Assistir Treinamentos',
          description: 'Como acessar e completar treinamentos online.',
          steps: [
            'Clique no treinamento desejado',
            'Assista ao vídeo ou leia o material',
            'Alguns treinamentos têm quiz ao final',
            'Ao completar, o certificado é gerado automaticamente'
          ]
        },
        {
          title: 'Certificados',
          description: 'Acesse seus certificados de conclusão.',
          steps: [
            'Treinamentos concluídos geram certificado',
            'Acesse em "Meus Treinamentos" → "Concluídos"',
            'Baixe o PDF do certificado quando necessário',
            'O RH também tem acesso para comprovação'
          ]
        },
        {
          title: 'DDS - Diálogo Diário de Segurança',
          description: 'Participe dos diálogos diários sobre segurança.',
          steps: [
            'DDS são treinamentos rápidos (5-15 min)',
            'Aborda temas de segurança do dia a dia',
            'Podem incluir vídeos curtos',
            'Registre sua participação clicando em "Concluir"'
          ]
        }
      ]
    }
  ];

  // Seções para ADMINISTRADORES/GESTORES
  const adminSections = [
    {
      id: 'gestaoSST',
      title: 'Gestão SST (NR-1)',
      icon: Shield,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      content: [
        {
          title: 'Visão Geral do GRO/PGR',
          description: 'Entenda o Gerenciamento de Riscos Ocupacionais exigido pela NR-1.',
          steps: [
            'O GRO (Gerenciamento de Riscos Ocupacionais) é obrigatório para todas as empresas desde janeiro/2022',
            'O PGR (Programa de Gerenciamento de Riscos) é o documento base do GRO',
            'O sistema CERP Brasil automatiza a criação do PGR com base nos riscos cadastrados',
            'O PGR deve ser revisado anualmente ou quando houver alterações significativas',
            'Acesse o módulo "PGR" no menu lateral para gerenciar seu programa'
          ]
        },
        {
          title: 'Inventário de Riscos',
          description: 'Cadastre e gerencie os riscos ocupacionais identificados na empresa.',
          steps: [
            'Acesse "Riscos" no menu lateral',
            'Clique em "Novo Risco" para cadastrar',
            'Selecione o tipo: Físico, Químico, Biológico, Ergonômico, Psicossocial ou de Acidentes',
            'Defina a fonte geradora, exposição e trabalhadores expostos',
            'Classifique o nível de risco: Baixo, Médio, Alto ou Crítico',
            'Cadastre as medidas de controle existentes e planejadas',
            'O sistema calcula automaticamente o status de conformidade NR-1'
          ]
        },
        {
          title: 'Planos de Ação',
          description: 'Gerencie as ações corretivas e preventivas para controle de riscos.',
          steps: [
            'Acesse "Planos de Ação" no menu lateral',
            'Cada risco pode ter múltiplas ações associadas',
            'Defina: responsável, prazo, prioridade e status',
            'Acompanhe o progresso das ações no Dashboard',
            'Ações vencidas geram alertas automáticos',
            'Evidências podem ser anexadas para comprovação'
          ]
        },
        {
          title: 'Geração do PGR em PDF/Word',
          description: 'Exporte o Programa de Gerenciamento de Riscos completo.',
          steps: [
            'Acesse "PGR" no menu lateral',
            'Verifique se todos os riscos e ações estão atualizados',
            'Clique em "Gerar PGR" para criar o documento',
            'Escolha o formato: PDF ou Word (editável)',
            'O documento segue o modelo oficial do MTE',
            'Inclui: identificação da empresa, inventário de riscos, planos de ação e cronograma',
            'Importante: O sistema gera o documento, mas a responsabilidade técnica é do profissional de SST habilitado'
          ]
        },
        {
          title: 'Análise de Riscos com IA',
          description: 'Utilize inteligência artificial para análise avançada de riscos.',
          steps: [
            'Acesse "Análise IA" no menu lateral',
            'O sistema analisa padrões nos seus dados de riscos e afastamentos',
            'Receba recomendações automáticas de medidas de controle',
            'Identifique riscos emergentes baseado em tendências',
            'A IA sugere priorização de ações baseada em criticidade'
          ]
        }
      ]
    },
    {
      id: 'empresa',
      title: 'Configuração da Empresa',
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      content: [
        {
          title: 'Dados Cadastrais',
          description: 'Configure os dados básicos da sua empresa como CNPJ, razão social, endereço e contatos.',
          steps: [
            'Acesse o menu "Perfil Empresa" no painel lateral',
            'Preencha todos os campos obrigatórios (marcados com *)',
            'Faça upload do logo da empresa para personalizar relatórios',
            'Clique em "Salvar Alterações" para confirmar'
          ]
        },
        {
          title: 'Plano e Assinatura',
          description: 'Gerencie seu plano, visualize limites e realize upgrades.',
          steps: [
            'Acesse "Assinatura" no menu lateral',
            'Visualize o uso atual de funcionários vs. limite do plano',
            'Compare os planos disponíveis (Starter, Professional, Enterprise, Master)',
            'Utilize PIX, Boleto ou Cartão de Crédito para pagamento'
          ]
        },
        {
          title: 'Integrações',
          description: 'Conecte serviços externos como e-Social, WhatsApp (Z-API) e armazenamento em nuvem.',
          steps: [
            'Acesse "Integrações" no menu',
            'Configure as chaves de API necessárias para cada serviço',
            'Teste a conexão antes de salvar',
            'Ative/desative integrações conforme necessário'
          ]
        }
      ]
    },
    {
      id: 'funcionarios',
      title: 'Gestão de Funcionários',
      icon: Users,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      content: [
        {
          title: 'Cadastro de Funcionários',
          description: 'Adicione colaboradores individualmente ou em massa via importação.',
          steps: [
            'Acesse "Funcionários" no menu lateral',
            'Clique em "Novo Funcionário" para cadastro individual',
            'Preencha dados pessoais, cargo, setor e contatos',
            'Para importação em massa, use o botão "Importar Excel"'
          ]
        },
        {
          title: 'Envio de Acesso ao Portal',
          description: 'Envie credenciais de acesso para que funcionários respondam avaliações.',
          steps: [
            'Na lista de funcionários, localize o colaborador desejado',
            'Clique no ícone de envelope (✉️) ao lado do nome',
            'O sistema criará uma conta e enviará email com login e senha temporária',
            'O funcionário poderá acessar o Portal do Funcionário para responder avaliações'
          ]
        },
        {
          title: 'Setores e Cargos',
          description: 'Organize sua estrutura organizacional por setores e cargos.',
          steps: [
            'Ao cadastrar funcionários, defina o setor de atuação',
            'Os setores são criados automaticamente conforme cadastro',
            'Use filtros na lista de funcionários para visualizar por setor',
            'Relatórios podem ser gerados por setor específico'
          ]
        },
        {
          title: 'Offboarding e Desligamentos',
          description: 'Gerencie o processo de desligamento de funcionários.',
          steps: [
            'Acesse "Offboarding" no menu lateral',
            'Registre o desligamento com data e motivo',
            'O sistema bloqueia automaticamente o acesso ao Portal',
            'Treinamentos e avaliações pendentes são cancelados',
            'Histórico do funcionário é preservado para relatórios'
          ]
        }
      ]
    },
    {
      id: 'avaliacoes',
      title: 'Avaliações Psicossociais',
      icon: Brain,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      borderColor: 'border-violet-200',
      content: [
        {
          title: 'Como Criar uma Nova Avaliação',
          icon: ListChecks,
          description: 'Crie avaliações personalizadas ou utilize o template COPSOQ II para análise de riscos psicossociais.',
          steps: [
            'Acesse "Avaliações" no menu lateral',
            'Clique no botão "Nova Avaliação"',
            'Escolha entre: "Criar do Zero" ou "Usar Template COPSOQ II"',
            'Para template COPSOQ II: as 25 perguntas validadas cientificamente são carregadas automaticamente',
            'Para avaliação personalizada: defina título, descrição e adicione suas perguntas',
            'Configure a data limite para respostas',
            'Clique em "Criar Avaliação" para salvar'
          ]
        },
        {
          title: 'Formatos de Pergunta',
          icon: ClipboardCheck,
          description: 'Entenda os diferentes tipos de perguntas disponíveis para suas avaliações.',
          steps: [
            'Escala Likert (1-5): Ideal para medir intensidade ou frequência ("Nunca" até "Sempre")',
            'Escala 1-4: Variação sem ponto neutro, força uma posição',
            'Sim/Não: Perguntas diretas com resposta binária',
            'Múltipla Escolha: Várias opções onde apenas uma pode ser selecionada',
            'Texto Livre: Permite respostas dissertativas para feedback qualitativo',
            'Dica: Use escalas para dados quantitativos e texto livre para sugestões'
          ]
        },
        {
          title: 'Métodos de Envio',
          icon: Send,
          description: 'Convide funcionários para responder as avaliações via diferentes canais.',
          steps: [
            'Na lista de avaliações, clique no ícone de envio (✉️)',
            'Selecione os funcionários por setor ou individualmente',
            'Escolha o método de envio:',
            '  • WhatsApp: Envia link direto pelo Z-API (requer configuração em Integrações)',
            '  • E-mail: Envia convite com link para resposta',
            '  • Ambos: Envia pelos dois canais simultaneamente',
            'Acompanhe o status de entrega na aba "Participação"',
            'Reenvie convites para quem não respondeu clicando em "Lembrete"'
          ]
        },
        {
          title: 'Dashboard de Participação',
          icon: PieChart,
          description: 'Monitore em tempo real quem respondeu e quem está pendente.',
          steps: [
            'Na lista de avaliações, clique no ícone de pessoas (👥)',
            'Visualize: Total de Enviados, Respondidos e Pendentes',
            'Veja a taxa de participação geral e por setor',
            'Gráfico de barras mostra progresso de cada setor',
            'Aba "Responderam": Lista funcionários que já completaram',
            'Aba "Pendentes": Lista quem ainda precisa responder',
            'Use o botão "Sincronizar Contadores" se os números parecerem desatualizados'
          ]
        },
        {
          title: 'Entendendo os Resultados (Semáforo COPSOQ)',
          icon: AlertTriangle,
          description: 'Interprete os gráficos de risco usando a metodologia internacional COPSOQ II.',
          steps: [
            'Clique no ícone de gráfico (📊) na avaliação desejada',
            'O sistema classifica cada dimensão em cores:',
            '  🟢 Verde (≥3.67): Situação favorável - risco baixo',
            '  🟡 Amarelo (2.34-3.66): Situação intermediária - atenção necessária',
            '  🔴 Vermelho (≤2.33): Situação crítica - ação urgente requerida',
            'Índice Geral (1-5): Média ponderada de todas as respostas',
            'Resultados por Setor: Compare diferentes áreas da empresa',
            'Use os dados para priorizar ações no PGR'
          ]
        },
        {
          title: 'Laudos Individuais (LGPD)',
          icon: FileText,
          description: 'Gere relatórios individuais confidenciais para acompanhamento de funcionários.',
          steps: [
            'Nos resultados da avaliação, clique em "Laudos Individuais"',
            'Selecione o funcionário desejado',
            'O laudo PDF inclui: resultados por dimensão, interpretação e recomendações',
            'IMPORTANTE: Este documento é confidencial (LGPD)',
            'Acesso registrado em log de auditoria',
            'Use para encaminhamento a profissionais de saúde mental quando necessário'
          ]
        },
        {
          title: 'Sincronização com PGR',
          icon: Target,
          description: 'Leve os achados da avaliação diretamente para o Inventário de Riscos.',
          steps: [
            'Nos resultados da avaliação, clique em "Sincronizar com PGR"',
            'O sistema identifica automaticamente os fatores de risco detectados',
            'Riscos psicossociais são categorizados: estresse, assédio, carga mental, etc.',
            'Cada risco é adicionado ao Inventário de Riscos com:',
            '  • Descrição do perigo identificado',
            '  • Nível de risco (baseado nas respostas)',
            '  • Medidas de controle sugeridas',
            'Acompanhe no módulo GRO/PGR as ações corretivas'
          ]
        },
        {
          title: 'Relatórios em PDF',
          icon: Download,
          description: 'Gere e baixe laudos profissionais para documentação e auditorias.',
          steps: [
            'Na avaliação, clique em "Baixar PDF dos Resultados"',
            'O relatório inclui:',
            '  • Dados da empresa e período da avaliação',
            '  • Estatísticas de participação',
            '  • Gráficos de risco por dimensão (semáforo)',
            '  • Análise por setor',
            '  • Recomendações baseadas nos resultados',
            'O logo da empresa é inserido automaticamente',
            'Use como evidência documental para fiscalizações NR-1'
          ]
        }
      ]
    },
    {
      id: 'aep',
      title: 'AEP - Avaliação Ergonômica Preliminar',
      icon: ClipboardCheck,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      content: [
        {
          title: 'O que é AEP?',
          description: 'A Avaliação Ergonômica Preliminar é exigida pela NR-17 para identificar riscos ergonômicos nos postos de trabalho.',
          steps: [
            'É obrigatória para todas as empresas conforme NR-17',
            'Deve ser realizada por setor/posto de trabalho',
            'Identifica inadequações de mobiliário, postura, iluminação, etc.',
            'Gera plano de ação para correção das inadequações'
          ]
        },
        {
          title: 'Criando uma Avaliação AEP',
          description: 'Passo a passo para criar e aplicar uma avaliação ergonômica.',
          steps: [
            'Acesse "AEP (Ergonomia)" no menu lateral',
            'Clique em "Nova Avaliação"',
            'Preencha o setor, local e responsável pela avaliação',
            'Responda o checklist de 40 itens baseado na NR-17',
            'O sistema calcula automaticamente o percentual de adequação',
            'Visualize o gráfico radar com os 8 domínios de ergonomia'
          ]
        },
        {
          title: 'Sincronização AEP → PGR',
          description: 'Integre os achados ergonômicos ao Programa de Gerenciamento de Riscos.',
          steps: [
            'Na AEP concluída, clique em "Sincronizar com PGR"',
            'O sistema converte automaticamente as inadequações em riscos ergonômicos',
            'Nível de risco é calculado pelo percentual de itens inadequados',
            'Planos de ação são gerados com medidas de controle sugeridas',
            'Os riscos aparecem no Inventário de Riscos com origem "AEP - NR-17"'
          ]
        },
        {
          title: 'Exportando Relatório AEP',
          description: 'Gere documentos PDF profissionais para evidência documental.',
          steps: [
            'Na lista de avaliações, localize a AEP desejada',
            'Clique no botão "Exportar PDF"',
            'O relatório inclui: dados do setor, checklist, gráfico radar e plano de ação',
            'O logo da empresa é incluído automaticamente no cabeçalho'
          ]
        }
      ]
    },
    {
      id: 'treinamentos',
      title: 'Treinamentos & DDS',
      icon: GraduationCap,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      content: [
        {
          title: 'Cadastro de Treinamentos Formais',
          description: 'Registre treinamentos obrigatórios e opcionais de segurança.',
          steps: [
            'Acesse "Treinamentos" no menu lateral',
            'Clique em "Novo Treinamento"',
            'Preencha: nome, descrição, carga horária e validade',
            'Defina se é obrigatório por NR ou opcional',
            'Adicione conteúdo: vídeos, PDFs ou links externos',
            'Salve o treinamento no sistema'
          ]
        },
        {
          title: 'Vincular e Enviar para Funcionários',
          description: 'Associe treinamentos aos colaboradores que devem realizá-los.',
          steps: [
            'Abra os detalhes do treinamento',
            'Clique em "Enviar Treinamento"',
            'Selecione os colaboradores por setor ou individualmente',
            'Escolha envio por WhatsApp, E-mail ou ambos',
            'Defina a data limite para conclusão',
            'O sistema enviará lembretes automáticos conforme configuração'
          ]
        },
        {
          title: 'DDS - Diálogo Diário de Segurança',
          description: 'Gerencie conteúdos de DDS para conscientização diária.',
          steps: [
            'Acesse "DDS" no menu lateral',
            'Clique em "Novo DDS" para criar um diálogo',
            'Preencha: título, categoria e conteúdo (suporta Rich Text com imagens)',
            'Adicione vídeo do YouTube se desejar (opcional)',
            'Envie para funcionários via WhatsApp ou E-mail',
            'Funcionários confirmam participação clicando no link recebido',
            'Acompanhe presença na lista de participantes'
          ]
        },
        {
          title: 'Controle de Vencimentos',
          description: 'Acompanhe treinamentos próximos do vencimento.',
          steps: [
            'O Dashboard exibe alertas de treinamentos vencendo em 30 dias',
            'A aba "Alertas" lista todos os vencimentos por período',
            'Configure notificações automáticas por email/WhatsApp',
            'Relatórios de compliance mostram status geral da empresa'
          ]
        },
        {
          title: 'Geração de Certificados',
          description: 'Emita certificados PDF automaticamente após conclusão.',
          steps: [
            'Ao marcar treinamento como concluído, o certificado é gerado',
            'Configure o modelo do certificado em "Configurações"',
            'Personalize com logo, assinaturas e dados do instrutor',
            'Funcionários podem baixar seus certificados no Portal',
            'Exporte relatórios com todos os certificados emitidos para auditorias'
          ]
        }
      ]
    },
    {
      id: 'dashboards',
      title: 'Dashboards e Relatórios',
      icon: BarChart3,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      content: [
        {
          title: 'Dashboard Principal',
          description: 'Visão geral dos indicadores de SST da sua empresa.',
          steps: [
            'O Dashboard é a primeira tela ao fazer login',
            'Exibe KPIs principais: Total de Riscos, Riscos Críticos, Avaliações, Funcionários',
            'Status de Conformidade NR-1: verde (conforme) ou laranja (atenção)',
            'Gráficos de distribuição de riscos por nível e tipo',
            'Widget de treinamentos com progresso geral'
          ]
        },
        {
          title: 'Painel Global (Superadmin)',
          description: 'Visão consolidada de todas as empresas gerenciadas.',
          steps: [
            'Disponível apenas para usuários superadmin',
            'Métricas agregadas: total de empresas, funcionários, MRR',
            'Lista de empresas com status de assinatura',
            'Busca por nome, CNPJ ou email do administrador',
            'Botão "Liberar Acesso" para ativar assinaturas manualmente'
          ]
        },
        {
          title: 'Relatórios de Compliance',
          description: 'Gere relatórios detalhados para auditorias e fiscalizações.',
          steps: [
            'Acesse "Relatórios" no menu lateral',
            'Selecione o tipo de relatório desejado',
            'Relatórios disponíveis:',
            '  • Inventário de Riscos (listagem completa)',
            '  • Status de Treinamentos (por funcionário/setor)',
            '  • Histórico de Afastamentos',
            '  • Avaliações Psicossociais (resultados agregados)',
            'Filtre por período, setor ou status',
            'Exporte em PDF ou Excel'
          ]
        },
        {
          title: 'Analytics Avançado',
          description: 'Análises estatísticas para tomada de decisão.',
          steps: [
            'Acesse "Analytics" no menu lateral',
            'Visualize tendências históricas de riscos e afastamentos',
            'Compare métricas entre setores',
            'Identifique padrões sazonais',
            'Gráficos interativos com zoom e filtros'
          ]
        },
        {
          title: 'Sistema de Alertas',
          description: 'Receba notificações de eventos importantes.',
          steps: [
            'Acesse "Alertas" no menu lateral',
            'Alertas automáticos para:',
            '  • Treinamentos vencendo/vencidos',
            '  • Avaliações com baixa participação',
            '  • Riscos críticos sem plano de ação',
            '  • Vencimento de certificados',
            'Configure notificações por email nas preferências'
          ]
        }
      ]
    },
    {
      id: 'afastamentos',
      title: 'Gestão de Afastamentos',
      icon: UserMinus,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      content: [
        {
          title: 'Registrar Afastamento',
          description: 'Documente afastamentos por doença, acidente ou outras causas.',
          steps: [
            'Acesse "Afastamentos" no menu lateral',
            'Clique em "Novo Afastamento"',
            'Selecione o funcionário afastado',
            'Preencha: tipo (doença, acidente, licença), CID, datas de início e previsão de retorno',
            'Anexe atestados médicos se disponíveis'
          ]
        },
        {
          title: 'Tipos de Afastamento',
          description: 'Categorize corretamente para relatórios precisos.',
          steps: [
            'Doença Ocupacional: relacionada ao trabalho (gera CAT)',
            'Doença Comum: não relacionada ao trabalho',
            'Acidente de Trabalho: CAT deve ser emitida em até 24h',
            'Acidente de Trajeto: ocorrido no deslocamento casa-trabalho',
            'Licença Maternidade/Paternidade',
            'Outros: férias, afastamento voluntário, etc.'
          ]
        },
        {
          title: 'Indicadores de Absenteísmo',
          description: 'Monitore taxas de afastamento e identifique padrões.',
          steps: [
            'O Dashboard exibe taxa de absenteísmo mensal',
            'Relatórios detalham afastamentos por setor, tipo e período',
            'Identifique setores com maior incidência',
            'Correlacione com dados de avaliações psicossociais',
            'Use dados para ações preventivas de SST'
          ]
        },
        {
          title: 'Integração com e-Social',
          description: 'Envie eventos de afastamento para o governo.',
          steps: [
            'Configure a integração com e-Social em "Integrações"',
            'Afastamentos acima de 15 dias são enviados automaticamente',
            'Eventos S-2230 são gerados conforme layout oficial',
            'Acompanhe o status de envio na aba e-Social'
          ]
        }
      ]
    },
    {
      id: 'denuncias',
      title: 'Canal de Denúncias Anônimas',
      icon: MessageSquare,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      content: [
        {
          title: 'O que é o Canal de Denúncias?',
          description: 'Sistema anônimo para relatos de irregularidades, conforme LGPD e compliance.',
          steps: [
            'Permite que funcionários e terceiros relatem irregularidades anonimamente',
            'Atende requisitos de compliance e LGPD',
            'Categorias: Assédio Moral/Sexual, Fraude, Discriminação, Violação de Segurança, etc.',
            'Acesso público via URL: /denuncia/{slug-da-empresa}',
            'Denunciante recebe protocolo único (CERP-XXXX) para acompanhamento'
          ]
        },
        {
          title: 'Gerenciando Denúncias (Admin)',
          description: 'Como administrar e responder às denúncias recebidas.',
          steps: [
            'Acesse "Canal de Denúncias" em Configurações',
            'Dashboard mostra: Total, Pendentes, Em Análise, Concluídas',
            'Clique em uma denúncia para ver detalhes',
            'Adicione notas internas (só admins veem)',
            'Responda ao denunciante (ele vê pelo protocolo, sem saber quem respondeu)',
            'Atualize o status conforme investigação evolui'
          ]
        },
        {
          title: 'Garantia de Anonimato',
          description: 'Como o sistema protege a identidade do denunciante.',
          steps: [
            'Nenhum IP, cookie ou identificador é registrado',
            'Não há login requerido para denunciar',
            'Anexos são armazenados sem metadados identificadores',
            'A empresa vê apenas o conteúdo da denúncia e categoria',
            'O protocolo CERP permite acompanhamento sem identificação'
          ]
        }
      ]
    }
  ];

  // Selecionar seções baseado no role
  const sections = isEmployee ? employeeSections : adminSections;

  const filteredSections = sections.filter(section => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      section.title.toLowerCase().includes(searchLower) ||
      section.content.some(item => 
        item.title.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower)
      )
    );
  });

  return (
    <div className="space-y-6" data-testid="help-center-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-teal-600" />
            Central de Ajuda
          </h1>
          <p className="text-slate-600 mt-1">Guia completo de utilização do CERP Brasil NR1</p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar no guia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="help-search-input"
          />
        </div>
      </div>

      {/* Quick Tips - Personalizada por role */}
      <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Lightbulb className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-medium text-teal-800">Dica Rápida</h3>
              <p className="text-sm text-teal-700 mt-1">
                {isEmployee ? (
                  <>
                    Acesse &quot;Minhas Avaliações&quot; para ver avaliações pendentes e &quot;Meus Treinamentos&quot; para acompanhar 
                    seu progresso. Suas respostas são confidenciais e protegidas pela LGPD.
                  </>
                ) : (
                  <>
                    Comece configurando os dados da sua empresa em &quot;Perfil Empresa&quot;, depois cadastre os funcionários 
                    e aplique as avaliações de SST. O sistema calculará automaticamente os indicadores de compliance.
                  </>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-4">
        {filteredSections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections[section.id];
          
          return (
            <Card key={section.id} className={`overflow-hidden border-l-4 ${section.borderColor}`}>
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className={`w-full px-6 py-4 flex items-center justify-between ${section.bgColor} hover:opacity-90 transition-opacity`}
                data-testid={`section-${section.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 bg-white rounded-lg shadow-sm`}>
                    <Icon className={`h-5 w-5 ${section.color}`} />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">{section.title}</h2>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-slate-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-500" />
                )}
              </button>

              {/* Section Content */}
              {isExpanded && (
                <CardContent className="py-6 space-y-6">
                  {section.content.map((item, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 ${section.bgColor} rounded-full mt-0.5`}>
                          <CheckCircle2 className={`h-4 w-4 ${section.color}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-800">{item.title}</h3>
                          <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                        </div>
                      </div>
                      
                      {/* Steps */}
                      <div className="ml-9 space-y-2">
                        {item.steps.map((step, stepIndex) => (
                          <div key={stepIndex} className="flex items-start gap-2 text-sm">
                            <ArrowRight className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-600">{step}</span>
                          </div>
                        ))}
                      </div>
                      
                      {index < section.content.length - 1 && (
                        <hr className="border-slate-100 ml-9" />
                      )}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* No results */}
      {filteredSections.length === 0 && (
        <Card className="py-12 text-center">
          <HelpCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">Nenhum resultado encontrado</h3>
          <p className="text-slate-500 mt-1">Tente buscar por outros termos</p>
        </Card>
      )}

      {/* Footer Help */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-medium text-slate-800">Não encontrou o que procurava?</h3>
              <p className="text-sm text-slate-600">Entre em contato com nosso suporte para ajuda personalizada.</p>
            </div>
            <div className="flex gap-3">
              <a
                href="mailto:suporte@cerpbrasil.com.br"
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
              >
                <Shield className="h-4 w-4" />
                Contatar Suporte
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
